import { useCallback, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { pickDataAccess } from "dataAccess/pickDataAccess";
import { useSceneId } from "hooks/useSceneId";
import { selectBackendProvider, selectProcoreContext } from "slices/explorer";
import { telemetry } from "telemetry";

/**
 * Before the open scene's files are deleted: publishes the object data access picked as if the
 * scene had nothing offline, then disposes the offline one, which closes the BIM index worker and
 * gives back its lease. The old backend is disposed only once a replacement is published: a
 * disposed backend left on globals answers nothing until the next scene load. Read through a ref,
 * so the callback keeps its identity when the replacement is published and doesn't re-run the
 * delete it was called from.
 */
export function useReplaceDataAccessWithoutOffline() {
    const {
        state: { db, view, dataAccess },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const procoreContext = useAppSelector(selectProcoreContext);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    const dataAccessRef = useRef(dataAccess);
    dataAccessRef.current = dataAccess;

    return useCallback(async () => {
        const previous = dataAccessRef.current;

        if (!db || !view) {
            // Nothing to pick a replacement from, and so nothing that could still be asking it.
            await previous?.dispose();
            return;
        }

        const sceneVersionId = procoreContext?.sceneVersionId;

        try {
            const dataAccess = await pickDataAccess(
                db,
                view,
                undefined,
                procoreContext?.enableBimIndex ?? false,
                isProcoreBackendProvider && procoreContext && sceneVersionId
                    ? {
                          companyId: procoreContext.companyId,
                          projectId: procoreContext.projectId,
                          sceneId,
                          sceneVersionId,
                          sceneSliceId: procoreContext.sceneSliceId,
                      }
                    : undefined,
            );
            dispatchGlobals(explorerGlobalsActions.update({ dataAccess }));
            await previous?.dispose();
        } catch (e) {
            // Swallowed: a rejection would skip the delete's action reset and leave the UI stuck
            // mid-delete.
            telemetry.trackCaughtException({
                error: e,
                source: "offline.useHandleOffline.replaceDataAccess",
                severity: "error",
                category: "data",
            });
        }
    }, [db, view, procoreContext, isProcoreBackendProvider, sceneId, dispatchGlobals]);
}
